import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import isPossible from "libphonenumber-js";
import { AsYouType } from "libphonenumber-js";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { authClient } from "@/lib/auth-client";
import { api } from "@/lib/api";
import type { ClubInfo } from "./club-info-card";

// Validation schema (same as onboarding)
const editClubInfoSchema = z.object({
  name: z
    .string()
    .min(2, "Le nom du club est requis")
    .max(50, "Le nom du club ne peut pas dépasser 50 caractères"),
  phone: z.string().refine(isPossible, "Numéro de téléphone invalide"),
  email: z.string().email("Format d'email invalide"),
  logo: z.string().nullable(),
});

type EditClubInfoFormValues = z.infer<typeof editClubInfoSchema>;

interface EditClubInfoSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clubInfo: ClubInfo;
  onSuccess: () => void;
}

export function EditClubInfoSheet({
  open,
  onOpenChange,
  clubInfo,
  onSuccess,
}: EditClubInfoSheetProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(clubInfo.logo);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Mutations for image upload/delete
  const { mutateAsync: requestUpload } = api.useMutation("post", "/club/image");
  const { mutateAsync: deleteImage } = api.useMutation("delete", "/club/image");

  const form = useForm<EditClubInfoFormValues>({
    resolver: zodResolver(editClubInfoSchema),
    defaultValues: {
      name: clubInfo.name,
      phone: clubInfo.phone,
      email: clubInfo.email,
      logo: clubInfo.logo,
    },
  });

  // Reset form when clubInfo changes
  useEffect(() => {
    form.reset({
      name: clubInfo.name,
      phone: clubInfo.phone,
      email: clubInfo.email,
      logo: clubInfo.logo,
    });
    setLogoPreview(clubInfo.logo);
  }, [clubInfo, form]);

  const asYouType = new AsYouType({
    defaultCountry: "FR",
    defaultCallingCode: "33",
  });

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
        toast.error("Format non supporté", {
          description: "Utilisez un fichier PNG, JPEG ou WebP",
        });
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5_000_000) {
        toast.error("Fichier trop volumineux", {
          description: "La taille maximum est de 5 Mo",
        });
        return;
      }

      setLogoFile(file);

      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        setLogoPreview(dataUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setLogoFile(null);
    form.setValue("logo", null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit = async (data: EditClubInfoFormValues) => {
    setIsLoading(true);

    try {
      let logoUrl: string | undefined = clubInfo.logo || undefined;

      // Handle logo upload if a new file was selected
      if (logoFile) {
        // Get presigned URL from backend
        const uploadData = await requestUpload({
          body: {
            contentType: logoFile.type,
            fileSize: logoFile.size,
          },
        });

        if (!uploadData) {
          toast.error("Erreur lors de l'upload du logo");
          setIsLoading(false);
          return;
        }

        // Upload file to presigned URL
        const uploadResponse = await fetch(uploadData.uploadUrl, {
          method: "PUT",
          body: logoFile,
          headers: {
            "Content-Type": logoFile.type,
          },
        });

        if (!uploadResponse.ok) {
          toast.error("Erreur lors de l'upload du logo");
          setIsLoading(false);
          return;
        }

        logoUrl = uploadData.publicUrl;
      } else if (logoPreview === null && clubInfo.logo) {
        // Logo was removed - delete it from storage
        try {
          await deleteImage({
            body: { imageUrl: clubInfo.logo },
          });
        } catch {
          // Ignore error if delete fails
        }
        logoUrl = undefined;
      }

      // Update organization using better-auth
      const result = await authClient.organization.update({
        data: {
          name: data.name,
          phoneNumber: data.phone,
          clubEmail: data.email,
          logo: logoUrl,
        },
      });

      if (result.error) {
        toast.error("Erreur lors de la mise à jour", {
          description: result.error.message,
        });
        return;
      }

      onOpenChange(false);
      onSuccess();
      toast.success("Informations mises à jour avec succès");
    } catch {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setIsLoading(false);
    }
  };

  // Generate initials for fallback
  const initials = form
    .watch("name")
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="h-screen max-w-md! overflow-y-auto [&>button]:hidden">
        <SheetHeader className="sticky top-0 z-2 border-b bg-white shadow-xs">
          <SheetTitle>Modifier les informations</SheetTitle>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-4 px-8">
            {/* Logo upload */}
            <FormItem>
              <FormLabel>Logo du club</FormLabel>
              <div className="flex flex-col items-center gap-4">
                <div
                  className="group relative flex h-24 w-24 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-orange-300 hover:bg-orange-50"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {logoPreview ? (
                    <>
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                        <span className="text-xs font-medium text-white">Changer</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center text-gray-400">
                      <Upload className="h-6 w-6" />
                      <span className="mt-1 text-xs">{initials || "Logo"}</span>
                    </div>
                  )}
                </div>

                {logoPreview && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveLogo}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="mr-1 h-3 w-3" />
                    Supprimer le logo
                  </Button>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleLogoChange}
                />
              </div>
              <FormDescription className="text-center text-xs">
                PNG, JPEG ou WebP. 5 Mo max.
              </FormDescription>
            </FormItem>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Nom du club <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Nom du club" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Téléphone du club <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="+33 1 23 45 67 89"
                      {...field}
                      onChange={(e) => {
                        field.onChange(asYouType.input(e.target.value));
                        if (
                          ["01", "02", "03", "04", "05", "06", "07", "09"].includes(
                            e.target.value.slice(0, 2)
                          )
                        ) {
                          asYouType.reset();
                          field.onChange(asYouType.input("+33" + e.target.value.slice(1)));
                        }
                        if (e.target.value.length === 0) {
                          asYouType.reset();
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Email du club <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="contact@club.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter className="mt-8 px-8 gap-2">
              <SheetClose asChild>
                <Button type="button" variant="outline">
                  Annuler
                </Button>
              </SheetClose>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sauvegarde...
                  </>
                ) : (
                  "Sauvegarder"
                )}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
